import { useState, useEffect } from 'react';
import ErrorHandler from '@Root/ErrorHandler';
import Statistics from '@IssueDashboards/Statistics';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useMediaQuery } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import TicketCard from '@IssueConsoleRepresentationalDisplay/TicketCard';
import TicketView from '@IssueConsoleViews/TicketView';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import TicketFieldExpandService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketFieldExpandService';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './TicketsPage.scss';
import LinearProgress from '@mui/material/LinearProgress';

export default function TicketsPage(props) {
    const is0To768 = useMediaQuery('(max-width: 768px)');
    const is0To992 = useMediaQuery('(max-width: 992px)');
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [, setFormType] = useState({});
    const [ticketStatusList, setTicketStatusList] = useState([]);
    const [ticketList, setTicketList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [adminFieldList, setAdminFieldList] = useState([]);
    const [totalResult, setTotalResult] = useState(1);
    const [showClosedTickets, setShowClosedTickets] = useState(false);
    const [, setShowSearchLoader] = useState(true);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [sortBy, setSortBy] = useState({
        type: 'createTimeStamp',
        sort: 'desc'
    });
    const [anchorFieldPopup, setAnchorFieldPopup] = useState(null);
    const [fieldPopupText] = useState('');
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [statusList, setStatusList] = useState([0, 1, 2]);
    const [isSorting, setIsSorting] = useState(false); // rename usage to unified action loader
    const [deepLinkTicketId, setDeepLinkTicketId] = useState(props.autoOpenTicketId || null);

    const startActionLoad = () => setIsSorting(true);
    const endActionLoad = () => setIsSorting(false);

    let defaultFilter = {
        page: 0,
        size: 10,
        ticketFilter: {
            statusList: [0, 1, 2, 3, 4],
            kaiziqResolveConnectionIdList: [props.kaiziqResolveConnection.id],
            sortBy: 'createTimeStamp',
            sortAsc: false
        }
    };
    const [filters, setFilters] = useState(defaultFilter);

    let { currentProjectId } = useReduxProject();
    let { activeUserId } = useReduxActiveUser();
    let fieldFilter = {
        projectIdList: [currentProjectId],
        tableType: 'kaiziqResolveConnection',
        tableId: props.kaiziqResolveConnection.id,
        filterAttribute1List: []
    };

    const fieldService = new FieldService();
    const errorHandler = new ErrorHandler();
    const ticketService = new TicketService();
    const ticketFieldExpandService = new TicketFieldExpandService();
    const className = 'resolve__ticketsPage';

    useEffect(() => {
        renderData();
    }, [props.kaiziqResolveConnection?.id]);

    useEffect(() => {
        if (props.autoOpenTicketId) {
            setDeepLinkTicketId(props.autoOpenTicketId);
        }
    }, [props.autoOpenTicketId]);

    const renderData = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            let ticketStatusListObj = await ticketService.getAnalysisByForm(props.kaiziqResolveConnection?.id);
            ticketStatusListObj = JSON.parse(ticketStatusListObj.body);
            let kaiziqResolveConnectionDetails = JSON.parse(props.kaiziqResolveConnection?.details);
            let settingsObj = kaiziqResolveConnectionDetails.settings;
            setFormType(settingsObj.formType);

            let adminFilter = fieldFilter;
            adminFilter.filterAttribute1List = ['admin'];
            let filter = { fieldFilter: adminFilter };
            let adminFieldListObj = await fieldService.getListFiltered(filter);
            adminFieldListObj = JSON.parse(adminFieldListObj.body);
            setAdminFieldList(adminFieldListObj?.result);
            setTicketStatusList(ticketStatusListObj.statusList);
            setKaiziqResolveConnection(props.kaiziqResolveConnection);
            await renderTickets(showClosedTickets);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const renderTickets = async (closed) => {
        startActionLoad();
        try {
            let statusListObj = closed ? [0, 1, 2, 3, 4] : [0, 1, 3, 4];
            setStatusList(statusListObj);
            let filterObj = defaultFilter;
            filterObj.ticketFilter.ticketStatusList = statusListObj;
            if (!!props.selectedList) {
                setStatusList(props.selectedList);
                filterObj.ticketFilter.statusList = props.selectedList;
            }
            filterObj.activeUserId = activeUserId;
            filterObj.projectId = currentProjectId;
            let ticketListObj = await ticketFieldExpandService.getListFilteredByAssignee(filterObj);
            ticketListObj = JSON.parse(ticketListObj?.body);
            setTicketList(ticketListObj?.result?.ticketsList);
            setTotalPages(ticketListObj?.result?.totalPages);
            setTotalResult(ticketListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        endActionLoad();
    };

    const renderTicketsByFilter = async (filter) => {
        startActionLoad();
        try {
            let filterObj = filter;
            let ticketListObj = await ticketFieldExpandService.getListFiltered(filterObj);
            ticketListObj = JSON.parse(ticketListObj?.body);
            setTicketList(ticketListObj?.result?.ticketsList);
            setTotalPages(ticketListObj?.result?.totalPages);
            setTotalResult(ticketListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        endActionLoad();
    };

    const reRenderData = async (newPage, newSize) => {
        startActionLoad();
        try {
            setShowSearchLoader(true);
            let filterObj = filters;
            filterObj.page = newPage - 1;
            filterObj.size = newSize;
            let ticketListObj = await ticketFieldExpandService.getListFiltered(filterObj);
            ticketListObj = JSON.parse(ticketListObj?.body);
            setTicketList(ticketListObj?.result?.ticketsList);
            setTotalPages(ticketListObj?.result?.totalPages);
            setTotalResult(ticketListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        endActionLoad();
    };

    const handleSizeChange = (event) => {
        let newSize = event.target.value;
        setSize(newSize);
        reRenderData(page, newSize);
    };

    const renderStatusTickets = async (selectedStatus) => {
        startActionLoad();
        try {
            let newStatusList = [...statusList];
            if (newStatusList.includes(selectedStatus)) {
                newStatusList = newStatusList.filter((item) => item !== selectedStatus);
            } else {
                newStatusList.push(selectedStatus);
            }
            setStatusList(newStatusList);
            let filterObj = defaultFilter;
            filterObj.ticketFilter.statusList = newStatusList;
            let ticketListObj = await ticketFieldExpandService.getListFiltered(filterObj);
            ticketListObj = JSON.parse(ticketListObj?.body);
            setTicketList(ticketListObj?.result?.ticketsList);
            setTotalPages(ticketListObj?.result?.totalPages);
            setTotalResult(ticketListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        endActionLoad();
    };

    const handleFieldPopupClose = () => {
        setAnchorFieldPopup(null);
    };

    const handlePaginationClick = (e, v) => {
        setPage(v);
        reRenderData(v, size);
    };

    const handleSortClick = async (field) => {
        if (loading) return;
        startActionLoad();
        setLoading(true);
        let sortByObj = { ...sortBy };
        if (sortBy.type !== field) {
            sortByObj.type = field;
            sortByObj.sort = 'desc';
        } else {
            sortByObj.sort = sortByObj.sort === 'desc' ? 'asc' : 'desc';
        }
        setSortBy(sortByObj);
        try {
            let filterObj = { ...defaultFilter };
            filterObj.ticketFilter.sortBy = sortByObj.type;
            filterObj.ticketFilter.sortAsc = sortByObj.sort === 'asc';
            setPage(1);
            await renderTicketsByFilter(filterObj);
            setFilters(filterObj);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
            endActionLoad();
        }
    };

    const handleRefresh = async () => {
        startActionLoad();
        setRefreshing(true);
        await renderData();
        setRefreshing(false);
        endActionLoad();
    };

    const toggleShowClosedTickets = async () => {
        startActionLoad();
        await renderTickets(!showClosedTickets);
        setShowClosedTickets(prev => !prev);
        endActionLoad();
    };

    const clearFilters = async () => {
        startActionLoad();
        setFilters(defaultFilter);
        setPage(1);
        await renderTickets(showClosedTickets);
        endActionLoad();
    };

    const onFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleSearch = async () => {
        startActionLoad();
        try {
            setTicketList([]);
            let filterObj = { ...filters, page: 0 };
            setFilters(filterObj);
            let ticketListObj = await ticketFieldExpandService.getListFiltered(filterObj);
            ticketListObj = JSON.parse(ticketListObj?.body);
            setTicketList(ticketListObj?.result?.ticketsList);
            setTotalPages(ticketListObj?.result?.totalPages);
            setTotalResult(ticketListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        endActionLoad();
    };

    const SortIcon = ({ field }) => {
        if (sortBy.type !== field) return null;
        return sortBy.sort === 'desc' ? (
            <ArrowDownwardIcon fontSize="small" className={className + '__headerIcon'} />
        ) : (
            <ArrowUpwardIcon fontSize="small" className={className + '__headerIcon'} />
        );
    };

    const TableHeader = ({ field, label, onClick, className: customClass }) => (
        <Box
            className={`${className}__tableHeader ${customClass || ''}`}
            onClick={onClick && !loading ? () => handleSortClick(field) : undefined}
            sx={{ cursor: onClick ? (loading ? 'not-allowed' : 'pointer') : 'default', opacity: loading ? 0.6 : 1 }}
            aria-busy={loading ? 'true' : 'false'}
        >
            <Typography className={className + '__tableHeader__text'}>{label}</Typography>
            {onClick && <SortIcon field={field} />}
        </Box>
    );

    return (
        <div className={className}>
            <Box className={className + '__container'}>
                {/* Header Section */}
                <Box className={className + '__header'}>
                    <Box className={className + '__header__left'}>
                        <Typography variant="h5" className={className + '__header__title'}>
                            Tickets
                        </Typography>
                    </Box>
                    <Box className={className + '__header__actions'}>
                        <Tooltip title="Refresh tickets">
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className={refreshing ? 'rotating' : ''}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Statistics/Filter Section */}
                <Paper className={className + '__statistics'} elevation={0}>
                    <Statistics
                        ticketStatusList={ticketStatusList}
                        toggleShowClosedTickets={toggleShowClosedTickets}
                        showClosedTickets={showClosedTickets}
                        renderStatusTickets={renderStatusTickets}
                        statusList={statusList}
                        filters={filters}
                        onFilterChange={onFilterChange}
                        clearFilters={clearFilters}
                        handleSearch={handleSearch}
                    />
                </Paper>

                {/* Table Section */}
                <Paper className={className + '__table'} elevation={0} aria-busy={loading ? 'true' : 'false'}>
                    {/* Table Headers */}
                    {!is0To992 && (
                        <Box className={className + '__tableHeaders'}>
                            <TableHeader field="trackingId" label="Tag" onClick />
                            <TableHeader field="ticketTitle" label="Title" onClick className="tableHeader--title" />
                            <TableHeader field="status" label="Status" onClick />
                            <TableHeader field="createTimeStamp" label="Reported" onClick />
                            <TableHeader field="createdByEmail" label="Reporter" onClick />
                            <TableHeader label="Assignee" />
                            <TableHeader label="Label" className="tableHeader--label" />
                            {!!kaiziqResolveConnection?.details &&
                                JSON.parse(kaiziqResolveConnection?.details)?.fields?.client?.map(
                                    (field) =>
                                        field.type !== 'information' && (
                                            <TableHeader key={field.id} field={field.id} label={field.title} onClick />
                                        )
                                )}
                        </Box>
                    )}

                    {/* Unified action progress bar (sorting, filtering, refresh, status toggle, search, pagination) */}
                    {isSorting && <LinearProgress className={className + '__sortProgress'} />}

                    {/* Loading State */}
                    {loading && (
                        <Box className={className + '__loader'}>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    height={80}
                                    style={{ borderRadius: 12, marginBottom: 12 }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Ticket List */}
                    {!loading && ticketList?.length > 0 && (
                        <Box className={className + '__ticketList'}>
                            {ticketList.map((ticket) => (
                                <Box key={ticket.id} className={className + '__ticketItem'}>
                                    <TicketCard
                                        ticket={ticket}
                                        reloadData={() => renderData(true)}
                                        kaiziqResolveConnection={kaiziqResolveConnection}
                                        adminFieldList={adminFieldList}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Empty State */}
                    {!loading && ticketList?.length === 0 && (
                        <Box className={className + '__empty'}>
                            <FilterListIcon />
                            <Typography>No tickets found</Typography>
                            <Typography variant="caption">
                                Try adjusting your filters or search criteria
                            </Typography>
                        </Box>
                    )}

                    {/* Footer/Pagination */}
                    {!loading && ticketList?.length > 0 && (
                        <Box className={className + '__footer'}>
                            <Box className={className + '__footer__info'}>
                                <Typography className={className + '__footer__text'}>
                                    Showing {(page - 1) * size + 1}-
                                    {Math.min(page * size, totalResult)} of {totalResult}
                                </Typography>
                                <Box className={className + '__footer__sizeControl'}>
                                    <Typography className={className + '__footer__label'}>
                                        Per page:
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={size}
                                        onChange={handleSizeChange}
                                        className={className + '__footer__select'}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </Box>
                            </Box>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePaginationClick}
                                showFirstButton
                                showLastButton
                                size={is0To768 ? 'small' : 'medium'}
                                siblingCount={is0To768 ? 0 : 1}
                                className={className + '__pagination'}
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            <Popover
                open={Boolean(anchorFieldPopup)}
                anchorEl={anchorFieldPopup}
                onClose={handleFieldPopupClose}
                disableRestoreFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
            >
                <Box className={className + '__fieldPopup'}>{fieldPopupText}</Box>
            </Popover>

            {deepLinkTicketId && (
                <TicketView
                    open={true}
                    closePopup={() => {
                        setDeepLinkTicketId(null);
                        props.onAutoOpenComplete?.();
                    }}
                    ticketId={deepLinkTicketId}
                    formId={props.kaiziqResolveConnection?.formId}
                    reloadData={() => renderData(true)}
                />
            )}
        </div>
    );
}

